import styled from 'styled-components';
import { useGetGeoVersionFileQuery, useLazyGetGeoVersionFileQuery } from '@shared/api';
import {
  fmtDateTime,
  fmtScale,
  versionFileName,
  type GeoScheme,
  type GeoVersion,
  type GeoVersionFileKind,
} from '@entities/geodesy';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconDownload } from '@shared/ui/icons';
import { downloadBlob, useAction, useObjectUrl } from './hooks';
import { CellActions, DataTable, ErrorText, Muted, Num, Row, Stack, TableWrap } from './ui';

/** Текущая (последняя) версия схемы. */
export function latestVersion(scheme: GeoScheme): GeoVersion | null {
  if (scheme.versions.length === 0) return null;
  const current = scheme.versions.find((v) => v.version === scheme.current_version);
  if (current) return current;
  return scheme.versions.reduce((a, b) => (b.version > a.version ? b : a));
}

const Sheet = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Paper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  /* Миллиметровка под листом: подчёркивает, что это чертёж, а не картинка. */
  background-color: ${({ theme }) => theme.colors.surface};
  background-image:
    linear-gradient(${({ theme }) => theme.colors.border} 1px, transparent 1px),
    linear-gradient(90deg, ${({ theme }) => theme.colors.border} 1px, transparent 1px);
  background-size: 24px 24px;

  img {
    display: block;
    max-width: 100%;
    max-height: 72vh;
    object-fit: contain;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    box-shadow: ${({ theme }) => theme.shadow.md};
  }
`;

const Caption = styled.figcaption`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};

  a {
    font-weight: 600;
  }
`;

/** PNG первого листа версии — превью в карточке (байты — через RBAC-запрос). */
export function VersionPreview({ scheme, version }: { scheme: GeoScheme; version: GeoVersion }) {
  const { data, isFetching, isError } = useGetGeoVersionFileQuery(
    { id: scheme.id, version: version.version, kind: 'png' },
    { skip: !version.has_png },
  );
  const url = useObjectUrl(data);
  const alt = `Исполнительная схема ${scheme.number}, версия ${version.version}, лист 1`;
  return (
    <Sheet>
      <Paper>
        {!version.has_png ? (
          <Muted>Для этой версии нет превью — скачайте DXF или PDF.</Muted>
        ) : isError ? (
          <ErrorText>Превью не загрузилось.</ErrorText>
        ) : url ? (
          <img src={url} alt={alt} />
        ) : (
          <Muted>{isFetching ? 'Загрузка превью…' : ' '}</Muted>
        )}
      </Paper>
      <Caption>
        <span>
          Версия {version.version} ·{' '}
          {version.source === 'uploaded' ? 'загружена из CAD' : 'сформирована'} ·{' '}
          {fmtDateTime(version.created_at)}
          {version.scale ? ` · М ${fmtScale(version.scale)}` : ''}
          {version.sheets_total ? ` · листов: ${version.sheets_total}` : ''}
        </span>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer">
            Открыть в полном размере
          </a>
        )}
      </Caption>
    </Sheet>
  );
}

/** Кнопки скачивания файлов версии: `ИС_<номер>_v<n>.<ext>`. */
export function VersionDownloads({
  scheme,
  version,
  size = 'md',
}: {
  scheme: GeoScheme;
  version: GeoVersion;
  size?: 'sm' | 'md';
}) {
  const [trigger] = useLazyGetGeoVersionFileQuery();
  const { run, busy, error } = useAction();

  function download(kind: GeoVersionFileKind) {
    void run(async () => {
      const blob = await trigger({ id: scheme.id, version: version.version, kind }, true).unwrap();
      downloadBlob(blob, versionFileName(scheme.number, version.version, kind));
    }, 'Файл не скачался.');
  }

  const Buttons = size === 'sm' ? CellActions : Row;
  return (
    <Stack $gap={1}>
      <Buttons $gap={2}>
        {version.has_dxf && (
          <Button
            type="button"
            $variant="secondary"
            $size={size}
            onClick={() => download('dxf')}
            disabled={busy}
            aria-label={`Скачать DXF версии ${version.version}`}
          >
            <IconDownload size={16} /> DXF
          </Button>
        )}
        {version.has_dxf && (
          <Button
            type="button"
            $variant="secondary"
            $size={size}
            onClick={() => download('dwg')}
            disabled={busy}
            aria-label={`Скачать DWG 2010 версии ${version.version}`}
            title="DWG версии 2010 — открывается в любом AutoCAD с 2010 года и в nanoCAD"
          >
            <IconDownload size={16} /> DWG
          </Button>
        )}
        {version.has_pdf && (
          <Button
            type="button"
            $variant="secondary"
            $size={size}
            onClick={() => download('pdf')}
            disabled={busy}
            aria-label={`Скачать PDF версии ${version.version}`}
          >
            <IconDownload size={16} /> PDF
          </Button>
        )}
        {!version.has_dxf && !version.has_pdf && <Muted>Файлов нет</Muted>}
      </Buttons>
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Stack>
  );
}

const SourceCell = styled.div`
  min-width: 180px;
`;

const Warn = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  padding-left: ${({ theme }) => theme.spacing(2)};
  border-left: 2px solid ${({ theme }) => theme.colors.status.warning};
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Note = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Список версий: источник, итоги отклонений, предупреждения рендера, автор. */
export function VersionsTable({ scheme }: { scheme: GeoScheme }) {
  const versions = scheme.versions.slice().sort((a, b) => b.version - a.version);
  if (versions.length === 0) return <Muted>Версий пока нет.</Muted>;
  return (
    <TableWrap>
      <DataTable>
        <thead>
          <tr>
            <th className="num">Версия</th>
            <th>Источник</th>
            <th className="num">Масштаб</th>
            <th className="num">Превышений</th>
            <th className="num">Листов</th>
            <th>Автор, время</th>
            <th>Файлы</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.version}>
              <td className="num">
                v{v.version}
                {v.version === scheme.current_version && (
                  <>
                    {' '}
                    <Badge $tone="info">текущая</Badge>
                  </>
                )}
              </td>
              <td>
                <SourceCell>
                  {v.source === 'uploaded' ? 'из CAD' : 'сформирована'}
                  {v.comment && <Note>{v.comment}</Note>}
                  {v.warnings.map((w) => (
                    <Warn key={w}>{w}</Warn>
                  ))}
                </SourceCell>
              </td>
              <td className="num">{v.scale ? fmtScale(v.scale) : '—'}</td>
              <td className="num">
                {v.exceeded == null ? (
                  '—'
                ) : (
                  <Num $tone={v.exceeded > 0 ? 'danger' : undefined}>
                    {v.exceeded}
                    {v.measured != null ? ` из ${v.measured}` : ''}
                  </Num>
                )}
              </td>
              <td className="num">{v.sheets_total ?? '—'}</td>
              <td>
                {v.created_by_name ?? '—'}
                <Note>{fmtDateTime(v.created_at)}</Note>
              </td>
              <td>
                <VersionDownloads scheme={scheme} version={v} size="sm" />
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </TableWrap>
  );
}
